import {
  GridColumnVisibilityModel,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
  GridPinnedColumnFields,
} from '@mui/x-data-grid-premium'
import { useRouter } from 'next/router'
import { useMemo, useState } from 'react'
import { useFirstRenderValue } from '@omnea-app/fe-legacy/utils/hooks/useFirstRenderValue'
import { deserializeFilterModel } from '../../utils/serializeFilterModel'
import { transformOmneaFilterModelToMui } from '../../utils/transformMuiGridModels'
import { getTableStateFromLocalStorage } from '../helpers/getTableStateFromLocalStorage'

export interface Values {
  sortModel: GridSortModel
  filterModel: GridFilterModel
  paginationModel: GridPaginationModel
  columnVisibilityModel: GridColumnVisibilityModel
  pinnedColumns: GridPinnedColumnFields
}

type SetState<T> = React.Dispatch<React.SetStateAction<T>>
export interface Setters {
  setSortModel: SetState<GridSortModel>
  setFilterModel: SetState<GridFilterModel>
  setPaginationModel: SetState<GridPaginationModel>
  setColumnVisibilityModel: SetState<GridColumnVisibilityModel>
  setPinnedColumns: SetState<GridPinnedColumnFields>
}

export type GetControlledTableDefaultState = () => Partial<Values>

interface Props {
  tableName: string
  getDefaultState?: GetControlledTableDefaultState
  nonHideableColumns?: readonly string[]
  sanitizeRestoredState?: (values: Values) => Values
}

export function useControlledTableState(props: Props): [Values, Setters] {
  const { tableName, getDefaultState, nonHideableColumns, sanitizeRestoredState } = props
  const router = useRouter()

  const defaultState = useFirstRenderValue(getDefaultState)
  const fromFilterModelParam = router.query.fromFilterModel as string | undefined
  const initialState = useMemo(() => {
    const restored = gridStateToControlledState(tableName, defaultState, fromFilterModelParam, nonHideableColumns)
    return sanitizeRestoredState ? sanitizeRestoredState(restored) : restored
  }, [tableName, defaultState, fromFilterModelParam, nonHideableColumns, sanitizeRestoredState])

  const [sortModel, setSortModel] = useState(initialState.sortModel)
  const [filterModel, setFilterModel] = useState(initialState.filterModel)
  const [paginationModel, setPaginationModel] = useState(initialState.paginationModel)
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(initialState.columnVisibilityModel)
  const [pinnedColumns, setPinnedColumns] = useState(initialState.pinnedColumns)

  const values = useMemo(
    () => ({
      sortModel: sortModel.slice(0, 1), // Sort by only one column
      filterModel,
      paginationModel,
      columnVisibilityModel,
      pinnedColumns,
    }),
    [sortModel, filterModel, paginationModel, columnVisibilityModel, pinnedColumns]
  )
  const setters = useMemo(
    () => ({ setSortModel, setFilterModel, setPaginationModel, setColumnVisibilityModel, setPinnedColumns }),
    []
  )

  return [values, setters]
}

export function getEmptyValues(): Values {
  return {
    sortModel: [],
    filterModel: { items: [] },
    paginationModel: { page: 0, pageSize: 10 },
    columnVisibilityModel: {},
    pinnedColumns: { left: [], right: [] },
  }
}

function gridStateToControlledState(
  tableName: string,
  defaultState: Partial<Values> = {},
  fromFilterModelParam?: string,
  nonHideableColumns: readonly string[] = []
): Values {
  const stateFromLocalStorage = getTableStateFromLocalStorage(tableName)
  const emptyValues = getEmptyValues()

  const omneaFilterModel = deserializeFilterModel(fromFilterModelParam)
  const muiFilterModel = omneaFilterModel
    ? transformOmneaFilterModelToMui(omneaFilterModel)
    : (defaultState.filterModel ?? emptyValues.filterModel)

  return {
    sortModel: stateFromLocalStorage?.sorting?.sortModel ?? defaultState.sortModel ?? emptyValues.sortModel,
    filterModel: muiFilterModel,
    paginationModel:
      stateFromLocalStorage?.pagination?.paginationModel ?? defaultState.paginationModel ?? emptyValues.paginationModel,
    columnVisibilityModel: {
      ...emptyValues.columnVisibilityModel,
      ...defaultState.columnVisibilityModel,
      ...dropPersistedHiddenLockedColumns(stateFromLocalStorage?.columns?.columnVisibilityModel, nonHideableColumns),
    },
    pinnedColumns: stateFromLocalStorage?.pinnedColumns ?? defaultState.pinnedColumns ?? emptyValues.pinnedColumns,
  }
}

// A hideable:false column has no toggle in the column menu, so a stale persisted `false` (e.g. left by the
// grouping-leaf fold) would hide it with no way to restore it. Drop those persisted entries only — the
// default model is spread first, so a column the table genuinely defaults to hidden stays hidden.
function dropPersistedHiddenLockedColumns(
  persisted: GridColumnVisibilityModel | undefined,
  nonHideableColumns: readonly string[]
): GridColumnVisibilityModel | undefined {
  if (!persisted || nonHideableColumns.length === 0) return persisted

  const sanitized = { ...persisted }
  for (const field of nonHideableColumns) {
    if (sanitized[field] === false) delete sanitized[field]
  }
  return sanitized
}
